import lod_ from "lodash";
import {
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	Fade,
	Icon,
	IconButton,
	InputAdornment,
	LinearProgress,
	TextField
} from "@mui/material";
import MDBox from "components/Basics/MDBox";
import { t } from "i18next";
import { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import MDTypography from "components/Basics/MDTypography";
import MDButton from "components/Basics/MDButton";
import { display } from "redux-react/reducers/snackBarReducer";
import { socket } from "redux-react/middleware/ws";
import { wsEvent } from "redux-react/actions/ws";
import { useMaterialUIController } from "context";
import ChartsActions from "redux-react/actions/chartsActions";
import OneValueChart from "../oneValueChart";
import ProportionChart from "../proportionChart";
import CrossTable from "../crossTable";
import DefaultDataTable from "components/Custom/Tables/DefaultDataTable";
import SettingsActions from "redux-react/actions/settingsActions";
import PageComponent from "pages/settings/charts/steps/4. DisplayChoiceStep/Components/display/PageComponent";
import IconComponent from "pages/settings/charts/steps/4. DisplayChoiceStep/Components/display/IconComponent";
import TitleField from "pages/settings/charts/steps/4. DisplayChoiceStep/Components/display/TitleField";
import DescriptionField from "pages/settings/charts/steps/4. DisplayChoiceStep/Components/display/DescriptionField";
import { v4 } from "uuid";

/**
 * Generate charts with AI dialog
 * @param {boolean} open - Open state
 * @param {function} onClose - Close function
 * @param {string} currentPage - Current page to import charts
 * @param {function} reloadPage - Function to reload the page after import
 * @returns {JSX.Element} - The GenerateChartsDialog component
 */
const GenerateChartsDialog = ({ open, onClose, currentPage, reloadPage }) => {
	const dispatch = useDispatch();
	const [controller] = useMaterialUIController();
	const { darkMode } = controller;
	const profile = useSelector(state => state.profile);

	const [generateText, setGenerateText] = useState("");
	const [threadID, setThreadID] = useState("");
	const [charts, setCharts] = useState({});
	const [loading, setLoading] = useState(false);
	const [previewChart, setPreviewChart] = useState(false);
	const [dictionary, setDictionary] = useState({});
	const [isRefreshingPreview, setIsRefreshingPreview] = useState(false);

	const [responseAgent, setResponseAgent] = useState("");

	const inputRef = useRef(null);

	const focusInput = () => {
		setTimeout(() => {
			inputRef.current?.focus();
			setGenerateText("");
		}, 1);
	};

	function getCorrectGraph(type) {
		if (!previewChart || lod_.isEmpty(charts)) return null;
		try {
			switch (type) {
				case "oneValue":
					return (
						<OneValueChart
							chart={{
								...charts,
								loaded: previewChart?.loaded
							}}
							data={previewChart?.data}
							dictionary={dictionary}
						/>
					);
				case "range":
				case "proportion":
				case "oneDimension":
				case "list":
					return (
						<ProportionChart
							chart={{
								...charts,
								loaded: previewChart?.loaded
							}}
							data={previewChart?.data}
						/>
					);
				case "crossTable":
				case "timeSeries":
				case "multiCollection":
					return (
						<CrossTable
							chart={{
								...charts,
								loaded: previewChart?.loaded
							}}
							data={previewChart?.data}
						/>
					);
				case "paginedList":
					return (
						<DefaultDataTable
							list={{
								...charts,
								loaded: previewChart?.loaded
							}}
							pagination={previewChart?.pagination}
							canSearch
							table={previewChart?.data}
							display={charts?.request?.attributesDisplay}
						/>
					);
				default:
					return null;
			}
		} catch (e) {
			return null;
		}
	}

	// Load chart
	const loadChart = generatedChart => {
		setIsRefreshingPreview(true);
		const onSuccess = res => {
			setIsRefreshingPreview(false);
			if (res?.charts?.length === 0) {
				setPreviewChart(false);
			} else {
				let resChart = res.charts[0];
				resChart.loaded = true;
				setPreviewChart(resChart);
			}
		};

		/**
		 * Before building chart, we need to check if all the required fields are filled
		 * it's depend on the type of the chart
		 */
		let canLoad = true;
		let pathsToCheck = [];
		switch (generatedChart?.type) {
			case "crossTable": {
				pathsToCheck = [
					"request.collection",
					"request.computeMethod",
					"request.row",
					"request.col"
				];
				break;
			}
			case "timeSeries": {
				pathsToCheck = [
					"request.binning",
					"request.dateAttribute",
					"request.collection",
					"request.computeMethod"
				];
				break;
			}
			default: {
				pathsToCheck = ["request.collection", "request.computeMethod"];
				break;
			}
		}

		canLoad = !pathsToCheck.some(path => {
			const value = lod_.get(generatedChart, path);
			return lod_.isNil(value) || lod_.isEmpty(value);
		});

		if (canLoad) {
			dispatch(ChartsActions.buildRawCharts([generatedChart], [], [], false, onSuccess));
		} else {
			setIsRefreshingPreview(false);
		}
	};

	const updateChartAttribute = (path, value) => {
		setCharts(prev => {
			const nextChart = lod_.cloneDeep(prev);
			lod_.set(nextChart, path, value);
			return nextChart;
		});
	};

	/**
	 * Load dictionary
	 * @param {string} codeDictionary - The code of the dictionary
	 * @returns {Promise} - The promise
	 */
	async function loadDictionary(codeDictionary) {
		return new Promise((resolve, reject) => {
			dispatch(
				SettingsActions.getDictionary(
					codeDictionary,
					res => {
						setDictionary({
							[codeDictionary]: res.dictionary
						});
						resolve();
					},
					err => reject(err)
				)
			);
		});
	}

	/**
	 * Handle the response from the WebSocket when charts are generated
	 */
	const handleChartsGenerated = useCallback(data => {
		const chartsData = data?.chart;
		if (lod_.isEmpty(chartsData)) {
			setLoading(false);
			setCharts({});
			setPreviewChart(false);
			setGenerateText("");
		} else {
			delete chartsData?.valid;
			if (chartsData.request?.collection) {
				loadDictionary(chartsData.request?.collection);
			}
			setCharts(data?.chart ?? {});
			loadChart(data?.chart);
			setGenerateText("");
			setThreadID(v4());
			setLoading(false);
		}
	}, []);

	/**
	 * Handle errors from the WebSocket during chart generation
	 */
	const handleChartsGenerationError = useCallback(
		data => {
			setResponseAgent("");
			setLoading(false);
			dispatch(display({ type: "error", message: data?.message ?? "Generation failed" }));
		},
		[dispatch]
	);

	/**
	 * Handle response from the WebSocket during chart generation from agent response
	 */
	const handleChartsGenerationInfoResponse = useCallback(data => {
		setLoading(false);
		focusInput();
		if (!lod_.isEmpty(data?.message)) {
			setResponseAgent(data.message);
		} else {
			setResponseAgent(t("SETTINGS.CHARTS.reformulate"));
			setThreadID(v4());
		}
	}, []);

	/**
	 * Listen for WebSocket events when the dialog is open
	 */
	useEffect(() => {
		if (!open || !socket) return undefined;

		socket.on("generate_charts_result", handleChartsGenerated);
		socket.on("generate_charts_info", handleChartsGenerationInfoResponse);
		socket.on("generate_charts_error", handleChartsGenerationError);

		return () => {
			socket.off("generate_charts_result", handleChartsGenerated);
			socket.off("generate_charts_info", handleChartsGenerationInfoResponse);
			socket.off("generate_charts_error", handleChartsGenerationError);
		};
	}, [
		open,
		handleChartsGenerated,
		handleChartsGenerationError,
		handleChartsGenerationInfoResponse
	]);

	/**
	 * Launch generation with AI with the text input and the current page as context
	 */
	const launchGeneration = () => {
		if (!generateText.trim()) return;
		setCharts({});
		setPreviewChart(false);
		setLoading(true);
		setResponseAgent("");

		dispatch(
			ChartsActions.generateChart(
				profile.assistantID,
				generateText,
				threadID,
				res => {},
				err => {
					setLoading(false);
					setCharts({});
				}
			)
		);
	};

	/**
	 * Add charts in the page selected after generation
	 */
	const addCharts = () => {
		const chartsToAdd = lod_.cloneDeep(charts);
		delete chartsToAdd.documentType;
		delete chartsToAdd.codeDictionary;
		chartsToAdd.active = true;
		dispatch(
			SettingsActions.newSetting(profile.assistantID, "chart", chartsToAdd, () => {
				reloadPage();
			})
		);
	};

	useEffect(() => {
		setGenerateText("");
		setCharts({});
		setPreviewChart(false);
		setLoading(false);
		setIsRefreshingPreview(false);
		setResponseAgent("");
		setThreadID(v4());
	}, [open]);

	// Theme-aware colors
	const bgMain = darkMode ? "#1a2035" : "#f7f8fc";
	const bgCard = darkMode ? "#202940" : "#ffffff";
	const borderColor = darkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)";
	const disabledBg = darkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)";
	const accentGradient = "linear-gradient(135deg, #1A73E8 0%, #6C63FF 100%)";

	const getButtonBackground = () => {
		if (!loading && generateText.trim()) return accentGradient;
		return disabledBg;
	};

	const getIconColor = () => {
		if (generateText.trim()) return "#fff";
		return darkMode ? "#555" : "#bbb";
	};

	return (
		<Dialog
			fullWidth
			maxWidth="xl"
			open={open}
			onClose={!loading ? onClose : undefined}
			PaperProps={{
				sx: {
					height: "90vh",
					maxWidth: "90vw",
					borderRadius: "16px",
					overflow: "hidden",
					background: bgMain
				}
			}}
		>
			{/* Header with gradient accent */}
			<MDBox
				sx={{
					background: accentGradient,
					px: 3,
					py: 2.5,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					position: "relative"
				}}
			>
				<MDBox display="flex" alignItems="center" gap={1.5}>
					<MDBox
						sx={{
							width: 40,
							height: 40,
							borderRadius: "12px",
							background: "rgba(255,255,255,0.2)",
							backdropFilter: "blur(10px)",
							display: "flex",
							alignItems: "center",
							justifyContent: "center"
						}}
					>
						<Icon sx={{ color: "#fff", fontSize: "22px !important" }}>auto_awesome</Icon>
					</MDBox>
					<MDBox>
						<MDTypography variant="h6" sx={{ color: "#fff", fontWeight: 700, lineHeight: 1.2 }}>
							{t("SETTINGS.CHARTS.generateChartsWithAI")}
						</MDTypography>
					</MDBox>
				</MDBox>
				<IconButton onClick={onClose} disabled={loading} sx={{ color: "#fff" }}>
					<Icon>close</Icon>
				</IconButton>
			</MDBox>

			{/* Loading bar */}
			{loading && (
				<LinearProgress
					sx={{
						height: 3,
						"& .MuiLinearProgress-bar": {
							background: accentGradient
						}
					}}
				/>
			)}

			{/* Content */}
			<DialogContent
				sx={{
					display: "flex",
					flexDirection: "column",
					flex: 1,
					p: 3,
					overflow: "hidden"
				}}
			>
				{/* Results area */}
				<MDBox
					flex={1}
					sx={{
						overflowY: "auto",
						mb: 2,
						borderRadius: "12px"
					}}
				>
					{/* Loading state */}
					{loading && (
						<Fade in>
							<MDBox
								display="flex"
								flexDirection="column"
								alignItems="center"
								justifyContent="center"
								height="100%"
								gap={2}
							>
								<MDBox
									sx={{
										width: 64,
										height: 64,
										borderRadius: "50%",
										background: accentGradient,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										animation: "pulse 2s ease-in-out infinite",
										"@keyframes pulse": {
											"0%, 100%": { transform: "scale(1)", opacity: 0.9 },
											"50%": { transform: "scale(1.08)", opacity: 1 }
										}
									}}
								>
									<Icon sx={{ color: "#fff", fontSize: "28px !important" }}>auto_awesome</Icon>
								</MDBox>
								<MDTypography variant="body2" fontWeight="medium" color="text">
									{t("SETTINGS.CHARTS.generatingCharts")}
								</MDTypography>
							</MDBox>
						</Fade>
					)}

					{/* Empty state */}
					{!loading && lod_.isEmpty(charts) && (
						<Fade in>
							<MDBox
								display="flex"
								flexDirection="column"
								alignItems="center"
								justifyContent="center"
								height="100%"
								gap={2}
							>
								<MDBox
									sx={{
										width: 80,
										height: 80,
										borderRadius: "20px",
										background: darkMode ? "rgba(26, 115, 232, 0.1)" : "rgba(26, 115, 232, 0.06)",
										display: "flex",
										alignItems: "center",
										justifyContent: "center"
									}}
								>
									<Icon
										sx={{
											fontSize: "36px !important",
											background: accentGradient,
											WebkitBackgroundClip: "text",
											WebkitTextFillColor: "transparent"
										}}
									>
										bar_chart
									</Icon>
								</MDBox>
								<MDBox textAlign="center">
									<MDTypography variant="h6" fontWeight="medium" color="text">
										{t("SETTINGS.CHARTS.noChartsGenerated")}
									</MDTypography>
								</MDBox>
							</MDBox>
						</Fade>
					)}

					{!loading && !lod_.isEmpty(charts) && previewChart && (
						<MDBox
							display="flex"
							style={{
								height: "100%"
							}}
						>
							{/* Preview - left */}
							<MDBox flex="3" className="displayLeftTab">
								<MDBox
									className="dialogContentChartEditor fullHeight"
									p={3}
									borderRadius="md"
									style={{
										overflow: "auto"
									}}
								>
									<MDBox flex="1" mt={4}>
										{!loading &&
											!lod_.isEmpty(charts) &&
											previewChart &&
											getCorrectGraph(charts.type)}
									</MDBox>
								</MDBox>
							</MDBox>

							{/* Inputs - right */}
							{!loading && !lod_.isEmpty(charts) && (
								<MDBox
									flex="2"
									ml={1}
									style={{
										overflow: "auto"
									}}
								>
									<MDBox mt={2}>
										{/* Titre */}
										<TitleField chart={charts} handleChange={updateChartAttribute} />
										{/* Description */}
										<DescriptionField chart={charts} handleChange={updateChartAttribute} />
										{/* Icon */}
										<IconComponent chart={charts} handleChange={updateChartAttribute} />
										{/* Pages */}
										<PageComponent chart={charts} handleChange={updateChartAttribute} />
									</MDBox>
								</MDBox>
							)}
						</MDBox>
					)}
				</MDBox>

				{/* Agent response */}
				{!lod_.isEmpty(responseAgent) && !loading && (
					<MDBox
						sx={{
							mb: 1.5,
							p: 2,
							borderRadius: "12px",
							background: darkMode ? "rgba(26,115,232,0.12)" : "rgba(26,115,232,0.08)",
							border: `1px solid ${darkMode ? "rgba(26,115,232,0.35)" : "rgba(26,115,232,0.25)"}`,
							display: "flex",
							alignItems: "flex-start",
							gap: 1.5
						}}
					>
						<MDBox
							sx={{
								width: 36,
								height: 36,
								borderRadius: "10px",
								background: accentGradient,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								flexShrink: 0
							}}
						>
							<Icon sx={{ color: "#fff", fontSize: "18px !important" }}>info</Icon>
						</MDBox>
						<MDBox>
							<MDTypography variant="body2" fontWeight="medium" color="text">
								{responseAgent}
							</MDTypography>
						</MDBox>
					</MDBox>
				)}

				{/* Input area — pinned at bottom */}
				<MDBox
					sx={{
						background: bgCard,
						borderRadius: "14px",
						border: `1px solid ${borderColor}`,
						p: 0.5,
						boxShadow: darkMode ? "0 -4px 20px rgba(0,0,0,0.2)" : "0 -4px 20px rgba(0,0,0,0.04)"
					}}
				>
					<TextField
						fullWidth
						multiline
						inputRef={inputRef}
						maxRows={4}
						placeholder={t("SETTINGS.CHARTS.generatePlaceholder")}
						value={generateText}
						onChange={e => setGenerateText(e.target.value)}
						onKeyDown={e => {
							if (e.key === "Enter" && !e.shiftKey) {
								e.preventDefault();
								launchGeneration();
							}
						}}
						disabled={loading}
						variant="standard"
						InputProps={{
							disableUnderline: true,
							sx: {
								px: 2,
								py: 1.5,
								fontSize: "0.95rem",
								"& textarea": {
									color: darkMode ? "#fff" : "#344767"
								},
								"& textarea::placeholder": {
									color: darkMode ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.35)",
									opacity: 1
								}
							},
							endAdornment: (
								<InputAdornment position="end">
									<IconButton
										onClick={launchGeneration}
										disabled={loading || !generateText.trim()}
										sx={{
											width: 38,
											height: 38,
											borderRadius: "10px",
											background: getButtonBackground(),
											transition: "all 0.2s ease",
											"&:hover": {
												opacity: 0.9
											},
											"&.Mui-disabled": {
												background: disabledBg
											}
										}}
									>
										{loading ? (
											<CircularProgress size={18} sx={{ color: "#1A73E8" }} />
										) : (
											<Icon
												sx={{
													fontSize: "20px !important",
													color: getIconColor()
												}}
											>
												arrow_upward
											</Icon>
										)}
									</IconButton>
								</InputAdornment>
							)
						}}
					/>
				</MDBox>
			</DialogContent>

			{/* Footer actions */}
			<DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
				<MDButton variant="outlined" color="dark" onClick={onClose}>
					{t("SETTINGS.close")}
				</MDButton>
				<MDButton
					disabled={lod_.isEmpty(charts) || lod_.isEmpty(charts?.display?.title) || loading}
					variant="contained"
					color="info"
					onClick={addCharts}
					sx={{
						background: accentGradient,
						"&:hover": { background: accentGradient, opacity: 0.9 }
					}}
				>
					<Icon sx={{ mr: 0.5 }}>add_circle</Icon>
					{t("SETTINGS.CHARTS.addAChart")}
				</MDButton>
			</DialogActions>
		</Dialog>
	);
};

export default GenerateChartsDialog;
